<template>
  <canvas
    ref="canvasEl"
    class="three-bg"
    aria-hidden="true"
  />
</template>

<script>
import * as THREE from 'three';
import { onBeforeUnmount, onMounted, ref } from 'vue';

const COUNT = 4200;
const RADIUS = 14;

export default {
  name: 'ThreeBackground',
  setup() {
    const canvasEl = ref(null);

    let renderer;
    let scene;
    let camera;
    let points;
    let pointsFar;
    let raf = 0;
    let mql;
    let matchListener;
    let onResize;
    let onMove;

    const targetCam = { x: 0, y: 0 };

    function isDark() {
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }

    function applyTheme(dark) {
      if (!scene) return;
      const bg = dark ? 0x04060d : 0xdde3ee;
      scene.background.setHex(bg);
      scene.fog.color.setHex(bg);
      if (points && points.material) {
        points.material.color.setHex(dark ? 0x6ec8ff : 0x3a6a9a);
        points.material.opacity = dark ? 0.9 : 0.55;
      }
      if (pointsFar && pointsFar.material) {
        pointsFar.material.color.setHex(dark ? 0xffc8a8 : 0x8a6040);
        pointsFar.material.opacity = dark ? 0.35 : 0.2;
      }
      scene.fog.density = dark ? 0.045 : 0.025;
    }

    function buildParticleLayers(dark) {
      const pos = new Float32Array(COUNT * 3);
      for (let i = 0; i < COUNT; i++) {
        const r = RADIUS * Math.cbrt(Math.random());
        const u = Math.random();
        const v = Math.random();
        const theta = u * Math.PI * 2;
        const phi = Math.acos(2 * v - 1);
        const si = i * 3;
        pos[si] = r * Math.sin(phi) * Math.cos(theta);
        pos[si + 1] = r * Math.sin(phi) * Math.sin(theta);
        pos[si + 2] = r * Math.cos(phi);
      }

      const nFar = Math.floor(COUNT / 3);
      const posFar = new Float32Array(nFar * 3);
      for (let i = 0; i < nFar; i++) {
        const r = RADIUS * 1.8 * (0.4 + Math.random());
        const theta = Math.random() * Math.PI * 2;
        const z = (Math.random() - 0.5) * 24;
        const si = i * 3;
        posFar[si] = r * Math.cos(theta);
        posFar[si + 1] = (Math.random() - 0.5) * 8;
        posFar[si + 2] = z;
      }

      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
      const mat = new THREE.PointsMaterial({
        size: 0.045,
        color: dark ? 0x6ec8ff : 0x3a6a9a,
        transparent: true,
        opacity: dark ? 0.9 : 0.55,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        sizeAttenuation: true,
      });

      const geoFar = new THREE.BufferGeometry();
      geoFar.setAttribute('position', new THREE.BufferAttribute(posFar, 3));
      const matFar = new THREE.PointsMaterial({
        size: 0.09,
        color: dark ? 0xffc8a8 : 0x8a6040,
        transparent: true,
        opacity: dark ? 0.35 : 0.2,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      });

      return { geo, mat, geoFar, matFar };
    }

    function animate(time) {
      raf = requestAnimationFrame(animate);
      const t = time * 0.00012;
      if (points) {
        points.rotation.y = t * 0.15;
        points.rotation.x = Math.sin(t * 0.08) * 0.12;
      }
      if (pointsFar) {
        pointsFar.rotation.y = -t * 0.06;
        pointsFar.rotation.z = Math.cos(t * 0.05) * 0.08;
      }
      if (camera) {
        camera.position.x += (targetCam.x - camera.position.x) * 0.04;
        camera.position.y += (targetCam.y - camera.position.y) * 0.04;
      }
      if (renderer && scene && camera) {
        renderer.render(scene, camera);
      }
    }

    function resize() {
      if (!renderer || !camera) return;
      const w = window.innerWidth;
      const h = window.innerHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    }

    onMounted(() => {
      const canvas = canvasEl.value;
      if (!canvas) return;

      const dark = isDark();
      scene = new THREE.Scene();
      scene.background = new THREE.Color(dark ? 0x04060d : 0xdde3ee);
      scene.fog = new THREE.FogExp2(dark ? 0x04060d : 0xdde3ee, dark ? 0.045 : 0.025);

      camera = new THREE.PerspectiveCamera(55, window.innerWidth / window.innerHeight, 0.1, 120);
      camera.position.z = 10;

      const built = buildParticleLayers(dark);
      points = new THREE.Points(built.geo, built.mat);
      pointsFar = new THREE.Points(built.geoFar, built.matFar);
      scene.add(points);
      scene.add(pointsFar);

      renderer = new THREE.WebGLRenderer({
        canvas,
        antialias: true,
        alpha: false,
        powerPreference: 'high-performance',
      });
      resize();

      onResize = () => resize();
      window.addEventListener('resize', onResize);

      onMove = (e) => {
        const nx = (e.clientX / window.innerWidth) * 2 - 1;
        const ny = (e.clientY / window.innerHeight) * 2 - 1;
        targetCam.x = nx * 0.35;
        targetCam.y = -ny * 0.25;
      };
      window.addEventListener('mousemove', onMove);

      mql = window.matchMedia('(prefers-color-scheme: dark)');
      matchListener = () => applyTheme(isDark());
      mql.addEventListener('change', matchListener);

      raf = requestAnimationFrame(animate);
    });

    onBeforeUnmount(() => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('mousemove', onMove);
      if (mql && matchListener) {
        mql.removeEventListener('change', matchListener);
      }

      if (points) {
        points.geometry.dispose();
        points.material.dispose();
      }
      if (pointsFar) {
        pointsFar.geometry.dispose();
        pointsFar.material.dispose();
      }
      if (renderer) {
        renderer.dispose();
      }

      renderer = null;
      scene = null;
      camera = null;
      points = null;
      pointsFar = null;
    });

    return { canvasEl };
  },
};
</script>

<style scoped>
.three-bg {
  position: fixed;
  inset: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  pointer-events: none;
  display: block;
}
</style>
